import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { Wallet, Plus, ArrowDownLeft, ArrowUpRight, Clock, CheckCircle, XCircle, Upload, AlertTriangle, Copy, ChevronLeft, ChevronRight, Lock } from 'lucide-react';

export default function WalletPage() {
  const { refreshUser } = useAuth();
  const [wallet, setWallet] = useState({ balance: 0, locked_referral_balance: 0, transactions: [], admin_upis: [], admin_upi: '', min_deposit: 0, min_withdraw: 0, has_deposited: false, profile_complete: false });
  const [form, setForm] = useState({ amount: '', type: 'add', note: '', utr_number: '' });
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [upiIndex, setUpiIndex] = useState(() => {
    // Random start index for UPI rotation
    return Math.floor(Math.random() * 100);
  });
  const fileRef = useRef(null);

  const fetchWallet = useCallback(async () => {
    try { const res = await api.get('/wallet'); setWallet(res.data); } catch {}
  }, []);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  // Auto-polling every 15 seconds
  useEffect(() => {
    const interval = setInterval(fetchWallet, 15000);
    return () => clearInterval(interval);
  }, [fetchWallet]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter valid amount'); return; }
    const amt = parseFloat(form.amount);
    if (form.type === 'add' && !wallet.profile_complete) {
      toast.error('Please complete your Profile (add UPI/Account) before deposit'); return;
    }
    if (form.type === 'add' && wallet.min_deposit > 0 && amt < wallet.min_deposit) {
      toast.error(`Minimum deposit: ${wallet.min_deposit} coins`); return;
    }
    if (form.type === 'withdraw' && wallet.min_withdraw > 0 && amt < wallet.min_withdraw) {
      toast.error(`Minimum withdrawal: ${wallet.min_withdraw} coins`); return;
    }
    if (form.type === 'add' && !form.utr_number) { toast.error('UTR number is required for deposit'); return; }
    setLoading(true);
    try {
      let screenshot_path = null;
      if (screenshotFile) {
        const fd = new FormData();
        fd.append('file', screenshotFile);
        const upRes = await api.post('/upload/screenshot', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        screenshot_path = upRes.data.path;
      }
      await api.post('/wallet/request', {
        amount: amt, type: form.type,
        note: form.note, utr_number: form.utr_number,
        screenshot_path
      });
      toast.success('Request submitted!');
      setForm({ amount: '', type: 'add', note: '', utr_number: '' });
      setScreenshotFile(null);
      if (fileRef.current) fileRef.current.value = '';
      fetchWallet();
      refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const currentUpi = wallet.admin_upis?.length > 0 ? wallet.admin_upis[upiIndex % wallet.admin_upis.length] : wallet.admin_upi || '';
  const totalUpis = wallet.admin_upis?.length || (wallet.admin_upi ? 1 : 0);

  const copyUpi = () => {
    if (currentUpi) { navigator.clipboard.writeText(currentUpi); toast.success('UPI copied!'); }
  };

  const statusIcon = (s) => {
    if (s === 'pending') return <Clock size={14} className="text-yellow-400" />;
    if (s === 'approved') return <CheckCircle size={14} className="text-green-400" />;
    return <XCircle size={14} className="text-red-400" />;
  };

  return (
    <div className="min-h-screen pb-28 p-4" style={{ background: 'var(--neon-bg)' }}>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Balance */}
        <div data-testid="wallet-balance-card" className="glass-card rounded-2xl p-6">
          <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--neon-text-muted)' }}>
            <Wallet size={12} className="inline mr-1" /> Available Balance
          </p>
          <h1 data-testid="wallet-balance" className="text-4xl font-black"
            style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-accent)' }}>
            {wallet.balance} <span className="text-lg" style={{ color: 'var(--neon-text-muted)' }}>coins</span>
          </h1>
          {wallet.locked_referral_balance > 0 && (
            <p className="text-xs mt-1" style={{ color: 'var(--neon-text-secondary)' }}>
              Locked Referral: {wallet.locked_referral_balance}
              {!wallet.has_deposited && <span style={{ color: 'var(--neon-secondary)' }}> (Deposit required to unlock)</span>}
            </p>
          )}
          {wallet.locked_deposit > 0 && (
            <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#FFD700' }}>
              <Lock size={12} /> Withdrawal locked: Pehle ₹{wallet.locked_deposit} ki bet lagao
            </p>
          )}
          <div className="flex gap-3 mt-2 text-xs" style={{ color: 'var(--neon-text-muted)' }}>
            {wallet.min_deposit > 0 && <span>Min Deposit: {wallet.min_deposit}</span>}
            {wallet.min_withdraw > 0 && <span>Min Withdraw: {wallet.min_withdraw}</span>}
          </div>
        </div>

        {/* Type Toggle */}
        <div className="flex gap-3">
          <button data-testid="wallet-add-btn" type="button"
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${form.type === 'add' ? 'btn-neon-primary' : 'btn-neon-secondary'}`}
            onClick={() => setForm(f => ({ ...f, type: 'add', utr_number: '' }))}>
            <Plus size={14} /> Add Coins
          </button>
          <button data-testid="wallet-withdraw-btn" type="button"
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${form.type === 'withdraw' ? 'btn-neon-primary' : 'btn-neon-secondary'}`}
            onClick={() => setForm(f => ({ ...f, type: 'withdraw', utr_number: '' }))}>
            <ArrowDownLeft size={14} /> Withdraw
          </button>
        </div>

        {/* Profile incomplete warning for deposit */}
        {form.type === 'add' && !wallet.profile_complete && (
          <div data-testid="profile-incomplete-warning" className="glass-card rounded-2xl p-5 text-center"
            style={{ border: '1px solid rgba(255,42,109,0.3)' }}>
            <AlertTriangle size={28} className="mx-auto mb-2" style={{ color: 'var(--neon-secondary)' }} />
            <p className="text-sm font-bold mb-1" style={{ color: 'var(--neon-secondary)' }}>Profile Incomplete</p>
            <p className="text-xs mb-3" style={{ color: 'var(--neon-text-muted)' }}>
              Please add your UPI ID or Account Number in Profile before making a deposit.
            </p>
            <a href="/profile" data-testid="go-to-profile-btn" className="btn-neon-primary text-sm px-6 py-2 inline-block rounded-lg">
              Go to Profile
            </a>
          </div>
        )}

        {/* Deposit: Show UPI Cards with QR */}
        {form.type === 'add' && wallet.profile_complete && totalUpis > 0 && (
          <div data-testid="admin-upi-card" className="glass-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-primary)' }}>
                Pay to this UPI
              </h3>
              {totalUpis > 1 && (
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--neon-elevated)', color: 'var(--neon-text-muted)' }}>
                  {upiIndex + 1} / {totalUpis}
                </span>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="bg-white p-3 rounded-xl">
                <QRCode value={`upi://pay?pa=${currentUpi}&pn=Mukamari&cu=INR`} size={140} />
              </div>
              <div className="flex-1">
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>UPI ID</p>
                <div className="flex items-center gap-2 mt-1">
                  <p data-testid="current-upi-display" className="font-bold text-lg break-all" style={{ color: 'var(--neon-accent)' }}>{currentUpi}</p>
                  <button data-testid="copy-upi-btn" onClick={copyUpi} className="p-1 rounded" style={{ color: 'var(--neon-primary)' }}>
                    <Copy size={14} />
                  </button>
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--neon-text-secondary)' }}>
                  Scan QR or copy UPI ID to make payment. Then submit UTR number below.
                </p>
              </div>
            </div>
            {/* UPI Navigation */}
            {totalUpis > 1 && (
              <div className="flex items-center justify-center gap-4 mt-4">
                <button data-testid="prev-upi-btn" onClick={() => setUpiIndex(i => Math.max(0, i - 1))} disabled={upiIndex === 0}
                  className="p-2 rounded-lg disabled:opacity-30" style={{ background: 'var(--neon-elevated)', color: 'var(--neon-primary)' }}>
                  <ChevronLeft size={16} />
                </button>
                <div className="flex gap-1.5">
                  {wallet.admin_upis.map((_, i) => (
                    <button key={i} onClick={() => setUpiIndex(i)}
                      className="w-2 h-2 rounded-full transition-all"
                      style={{ background: i === upiIndex ? 'var(--neon-primary)' : 'rgba(255,255,255,0.15)' }} />
                  ))}
                </div>
                <button data-testid="next-upi-btn" onClick={() => setUpiIndex(i => Math.min(totalUpis - 1, i + 1))} disabled={upiIndex >= totalUpis - 1}
                  className="p-2 rounded-lg disabled:opacity-30" style={{ background: 'var(--neon-elevated)', color: 'var(--neon-primary)' }}>
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}
        {form.type === 'add' && wallet.profile_complete && totalUpis === 0 && (
          <div data-testid="no-upi-warning" className="glass-card rounded-2xl p-5 text-center"
            style={{ border: '1px solid rgba(255,215,0,0.2)' }}>
            <AlertTriangle size={28} className="mx-auto mb-2" style={{ color: 'var(--neon-accent)' }} />
            <p className="text-sm font-bold mb-1" style={{ color: 'var(--neon-accent)' }}>Payment UPI Not Available</p>
            <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>
              Admin has not set up the payment UPI yet. Please contact admin or try again later.
            </p>
          </div>
        )}

        {/* Warning */}
        <div className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'rgba(255,215,0,0.08)', border: '1px solid rgba(255,215,0,0.2)' }}>
          <AlertTriangle size={16} style={{ color: 'var(--neon-accent)', flexShrink: 0, marginTop: 2 }} />
          <p className="text-xs" style={{ color: 'var(--neon-accent)' }}>
            {form.type === 'add'
              ? 'Incorrect UTR or fake screenshot will result in request rejection. Double check before submitting.'
              : 'Ensure your profile has correct UPI/Account details. Wrong info may cause rejection. Your profile payment info will be sent with this request.'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-6 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>
              Amount {form.type === 'add' && wallet.min_deposit > 0 ? `(Min: ${wallet.min_deposit})` : ''}{form.type === 'withdraw' && wallet.min_withdraw > 0 ? `(Min: ${wallet.min_withdraw})` : ''}
            </Label>
            <Input data-testid="wallet-amount-input" className="neon-input" type="number" required min="1"
              placeholder="Enter amount" value={form.amount}
              onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))} />
          </div>

          {form.type === 'add' && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>
                  UTR Number *
                </Label>
                <Input data-testid="wallet-utr-input" className="neon-input" required
                  placeholder="Enter UTR/Transaction number" value={form.utr_number}
                  onChange={(e) => setForm(f => ({ ...f, utr_number: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>
                  <Upload size={12} className="inline mr-1" /> Payment Screenshot
                </Label>
                <input data-testid="wallet-screenshot-input" type="file" accept="image/*" ref={fileRef}
                  className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-bold file:text-xs"
                  style={{ color: 'var(--neon-text-secondary)' }}
                  onChange={(e) => setScreenshotFile(e.target.files?.[0] || null)} />
              </div>
            </>
          )}

          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>Note (Optional)</Label>
            <Input data-testid="wallet-note-input" className="neon-input" placeholder="Add note"
              value={form.note} onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))} />
          </div>

          <button type="submit" data-testid="wallet-submit-btn" disabled={loading}
            className="w-full btn-neon-primary disabled:opacity-50">
            {loading ? 'Submitting...' : `Request ${form.type === 'add' ? 'Deposit' : 'Withdraw'}`}
          </button>
        </form>

        {/* Transaction History */}
        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-text)' }}>
            History
          </h3>
          {wallet.transactions.length === 0 ? (
            <p className="text-center py-6 text-sm" style={{ color: 'var(--neon-text-muted)' }}>No transactions</p>
          ) : (
            <div className="space-y-3">
              {wallet.transactions.map((tx, i) => (
                <div key={tx.id || i} data-testid={`transaction-${i}`}
                  className="p-3 rounded-lg" style={{ background: 'var(--neon-elevated)' }}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {tx.type === 'add' ? <ArrowDownLeft size={16} style={{ color: 'var(--neon-primary)' }} /> :
                        <ArrowUpRight size={16} style={{ color: 'var(--neon-secondary)' }} />}
                      <span className="text-sm font-bold" style={{ color: 'var(--neon-text)' }}>
                        {tx.type === 'add' ? 'Deposit' : 'Withdraw'}
                      </span>
                    </div>
                    <span className="font-bold" style={{ color: tx.type === 'add' ? 'var(--neon-primary)' : 'var(--neon-secondary)' }}>
                      {tx.type === 'add' ? '+' : '-'}{tx.amount}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs" style={{ color: 'var(--neon-text-muted)' }}>
                    <span>{new Date(tx.created_at).toLocaleString()}</span>
                    <div className="flex items-center gap-1">
                      {statusIcon(tx.status)} <span className="capitalize">{tx.status}</span>
                    </div>
                  </div>
                  {tx.admin_message && (
                    <p className="mt-2 text-xs p-2 rounded" style={{ background: 'var(--neon-surface)', color: 'var(--neon-text-secondary)' }}>
                      Admin: {tx.admin_message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
